import { DomElementAlignment, DomTargetPosition, OdinIcon, OdinIconSize, OverlayPanel } from '@myosh/odin-components';
import { t } from 'i18next';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useIsEditableMode } from '../Charts/hooks/useIsEditableMode';
import { OdinCustomFilters } from '../CustomFilterComponent/CustomFilterComponent';
import WidgetSettingsDialog from '../WidgetSettingWindow/widget-settings-dialog';
import { WidgetConfigsGenericWrapper } from './chart-generic-wrapper';
import ConfirmationWindow from './confirmation-window';

import { odinIconStyle } from '../../constants/common-style-constants';
import { LayoutWidget } from '../../redux/config/layout';
import { useUpdateLayoutWidgetDataMutation } from '../../redux/config/layout-widget';
import {
  useDeleteLayoutWidgetMutation,
  useGetWidgetInsightsEnabledQuery,
  useStartRecordsInsightsAnalysisMutation,
} from '../../redux/config/widgetApi';
import { selectActiveTabId } from '../../redux/slices/active-layout-tab-id-slice';
import { getTabs, getWidgets } from '../../redux/slices/layout-tabs-slice';
import { setWidgetWidthHeight } from '../../redux/slices/update-width-height-of-chart';

import SettingsMenu, { MenuItem } from './menu-item';

import * as htmlToImage from 'html-to-image';
import jsPDF from 'jspdf';
import { isEmpty } from 'lodash';
import toast from 'react-hot-toast';
import { v4 } from 'uuid';
import MyoshLogo from '../../assets/images/myosh_logo.png';
import { CHART_TYPE } from '../../constants/enums';
import { useConfiguredDateRanges } from '../../hooks/useConfiguredDateRanges';
import { CustomFiltersType } from '../../redux/config/CustomFiltersApi';
import { useGetUserAppliedFiltersQuery } from '../../redux/config/filters-api';
import { JobStatus, selectSubmittedJobs, setJobStatus, submitJob } from '../../redux/slices/running-jobs';
import { setWidgetAiInsightPanelOpened } from '../../redux/slices/widgetChart/WidgetChartSlice';
import { useExportFile } from '../Charts/hooks/use-export-file';

import { ComparedFrLineResponse } from '../../data-transformer/compared-fr-line-chart/compared-fr-line.interface';
import {
  BasicDataChartResponse,
  ChartColumnLineResponse,
  ChartDataKPI,
  ChartResponse,
  ColumnKpiPercentageResponse,
  ColumnRateResponse,
  MapChartData,
  NCRLineChartResponse,
} from '../../redux/config/chart-data';
import { CrossTabReportProps, GroupedTableType, TableDataResponse } from '../../redux/config/table-data.interfaces';
import { ChartDifferenceProps } from '../Charts/chart.interface';

/**
 * Widget chart data can have various shapes depending on the chart type.
 *
 * Known shapes:
 * - `ChartResponse[]` - Pie, Line, Bar, Column charts
 * - `ChartDataKPI` - KPI charts (has `basicValue` array property)
 * - `ChartColumnLineResponse` - Column/Line combo charts
 * - `ComparedFrLineResponse` - Compared FR line charts
 * - `ColumnRateResponse[]` - Column rate charts
 * - `ColumnKpiPercentageResponse[]` - KPI percentage charts
 * - `MapChartData[]` - Map widgets
 * - `NCRLineChartResponse` - NCR aging line charts
 * - `BasicDataChartResponse[]` - Basic data arrays
 * - `CrossTabReportProps` - Cross-tab report tables
 * - `ChartDifferenceProps` - Difference charts
 * - `GroupedTableType` - Grouped table data (injury site)
 * - `TableDataResponse` - Table rate data
 *
 * Use the type guards below to safely access data:
 * - isChartDataKPI() - checks for { basicValue: unknown[] } shape (ChartDataKPI)
 * - hasWidgetData() - checks if there's any data to display
 * - getWidgetDataBody() - extracts the data array regardless of shape
 */
export type WidgetChartData =
  | ChartResponse[]
  | ChartDataKPI
  | ChartColumnLineResponse
  | ComparedFrLineResponse
  | ColumnRateResponse[]
  | ColumnKpiPercentageResponse[]
  | MapChartData[]
  | NCRLineChartResponse
  | BasicDataChartResponse[]
  | CrossTabReportProps
  | ChartDifferenceProps
  | GroupedTableType
  | TableDataResponse
  | undefined;

/** Type guard to check if widgetChartData is ChartDataKPI (has basicValue array property) */
const isChartDataKPI = (data: WidgetChartData): data is ChartDataKPI =>
  typeof data === 'object' && data !== null && 'basicValue' in data && Array.isArray(data.basicValue);

/** Gets the data array from widgetChartData, handling both formats */
const getWidgetDataBody = (data: WidgetChartData): WidgetChartData | BasicDataChartResponse[] => {
  if (isChartDataKPI(data)) {
    return data.basicValue;
  }
  return data;
};

/** Checks if widgetChartData has any data */
const hasWidgetData = (data: WidgetChartData): boolean => {
  if (isChartDataKPI(data)) {
    return (data.basicValue?.length ?? 0) > 0;
  }
  return Array.isArray(data) && data.length > 0;
};

export interface ChartBottomPanelInterface {
  widgetId: string;
  refetch: () => void;
  widgetConfiguration?: WidgetConfigsGenericWrapper;
  isTable?: boolean;
  widgetChartData: WidgetChartData;
}

const canGenerateAiInsight = (type?: CHART_TYPE) => {
  if (
    type === CHART_TYPE.ChartBar ||
    type === CHART_TYPE.ChartColumn ||
    type === CHART_TYPE.ChartLine ||
    type === CHART_TYPE.ChartPie
  ) {
    return true;
  }
  return false;
};

const PDF_HORIZONTAL_MARGIN = 20;
const PDF_VERTICAL_MARGIN = 60;

const BACKGROUND_EXPORT_WIDGET_TYPES = [
  'TABLE',
  'TABLE_INJURY_FREE_DAYS',
  'MANAGEMENT_TABLE',
  'CROSS_TAB_REPORT',
  'TABLE_ROLLING_TRIFR',
  'TABLE_RATE',
  'INJURY_NUMBER_BY_SITE_TABLE',
];

export default function ChartBottomPanel({
  widgetId,
  refetch,
  isTable,
  widgetConfiguration,
  widgetChartData,
}: ChartBottomPanelInterface) {
  const iconRef = useRef<HTMLDivElement>(null);
  const toastId = useRef<string>(v4());
  const creatingJobsWidgetIds = useRef<string[]>([]);

  const [configOverlayOpened, setConfigOverlayOpened] = useState<boolean>(false);
  const [settingsOpened, setSettingsOpened] = useState<boolean>(false);
  const [showSaveModal, setShowSaveModal] = useState<boolean>(false);
  const [layoutWidget, setLayoutWidget] = useState<LayoutWidget>();
  const [showFooter, setShowFooter] = useState<boolean>(true);
  const [customFiltersData, setCustomFiltersData] = useState<Array<CustomFiltersType>>([]);

  const [deleteWidget, { isLoading: isDeleting }] = useDeleteLayoutWidgetMutation();
  const isEditable = useIsEditableMode();
  const [updateWidgetLayout] = useUpdateLayoutWidgetDataMutation();
  const { data: aiEnabled, isFetching: isConfigFetching } = useGetWidgetInsightsEnabledQuery();
  const dispatch = useDispatch();
  const { exportWidgetHandler } = useExportFile({ widgetId });
  const { data: userAppliedFilters } = useGetUserAppliedFiltersQuery();
  const dateRanges = useConfiguredDateRanges();

  const [startRecordsInsightsAnalysisJob] = useStartRecordsInsightsAnalysisMutation();

  const layoutTabs = useSelector(getTabs);
  const runningJobs = useSelector(selectSubmittedJobs);
  const layoutTabWidgets = useSelector(getWidgets);
  const activeTabId = useSelector(selectActiveTabId);

  const runningJobsRef = useRef(runningJobs);

  useEffect(() => {
    setLayoutWidget(layoutTabWidgets.find((widget) => +widget.widget.id === +widgetId));
  }, [layoutTabWidgets, widgetId]);

  const layoutProps = useMemo(() => {
    if (layoutTabs) {
      return layoutTabs.find((tab) => tab.id === activeTabId);
    }
  }, [activeTabId, layoutTabs]);

  const isAvailableToUser: boolean = useMemo(
    () => (layoutProps?.canModify || layoutProps?.isUserLayout ? isEditable : false),
    [layoutProps, isEditable]
  );

  const captureScreenshot = async () => {
    const widgetLayoutId = String(layoutWidget?.id);
    const node = document.getElementById(widgetLayoutId);

    if (node) {
      toast.loading(t('exportInProgress'));

      const doc = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a4',
      });

      const pageWidth = doc.internal.pageSize.getWidth();

      const logoProperties = doc.getImageProperties(MyoshLogo);
      const rescaleFactor = 0.1;
      const logoWidth = logoProperties.width * rescaleFactor;
      const logoHeight = logoProperties.height * rescaleFactor;
      doc.addImage(MyoshLogo, 'PNG', 5, 2.5, logoWidth, logoHeight);

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);

      doc.text(`${widgetConfiguration?.title}`, 20, 20);

      // Add date range to the PDF, preferring view-specific filters if available
      let dateRange = '';
      const { fromDate, toDate } = userAppliedFilters?.dashboardFiltersCombination?.dateFiltersForView || {};
      const dateFilterRangeType =
        userAppliedFilters?.dashboardFiltersCombination?.dateFiltersForView?.dateFilterRangeType;
      if (dateFilterRangeType) {
        const dates = dateRanges?.find((range) => range.id === dateFilterRangeType);
        dateRange = dates ? `${dates.from} - ${dates.to}` : '';
      } else if (fromDate && toDate) {
        dateRange = `${fromDate} - ${toDate}`;
      }

      if (dateRange) {
        const textWidth = doc.getTextWidth(dateRange);
        doc.text(dateRange, pageWidth - textWidth - 20, 20);
      }

      try {
        const dataUrl = await htmlToImage.toPng(node, {
          cacheBust: true,
          backgroundColor: 'white',
          pixelRatio: 2,
        });

        const imgProps = doc.getImageProperties(dataUrl);

        const pdfWidth = pageWidth - PDF_HORIZONTAL_MARGIN;
        const pdfHeight = doc.internal.pageSize.getHeight() - PDF_VERTICAL_MARGIN;

        let imgWidth = imgProps.width;
        let imgHeight = imgProps.height;

        // scale down proportionally
        const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
        imgWidth *= ratio;
        imgHeight *= ratio;

        const x = (pageWidth - imgWidth) / 2;
        const y = 30;

        doc.addImage(dataUrl, 'PNG', x, y, imgWidth, imgHeight);

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);

        let textY = y + imgHeight + 15;

        customFiltersData
          .filter((f) => f.currentValues && f.currentValues.length > 0)
          .forEach((field) => {
            const label = `${field.field}: ${(field.currentValues ?? []).join(', ')}`;
            doc.text(label, 20, textY);
            textY += 7;
          });

        doc.save(`${widgetConfiguration?.title}`);
        toast.dismiss();
        toast.success(t('exportComplete'));
      } catch (_error) {
        toast.dismiss();
        toast.error(t('exportFailed'));
      } finally {
        setShowFooter(true);
      }
    } else {
      toast.error(t('exportFailed'));
    }
  };

  const menuOptions: MenuItem[] = useMemo(
    () =>
      [
        {
          title: 'Record Insight',
          isSupported: true,
          isAvailableToUser:
            !isConfigFetching &&
            Boolean(aiEnabled) &&
            !isEmpty(widgetConfiguration?.hoursWorkedAIConfig) &&
            !isEmpty(widgetConfiguration?.injuryFrequencyAIConfig),
          icon: 'ClipboardList',
          clickHandler: () => handleClickRecordInsight(widgetConfiguration?.id, widgetConfiguration?.title),
        },
        {
          title: 'AI Insight',
          isSupported: true,
          isAvailableToUser:
            !isConfigFetching &&
            Boolean(aiEnabled) &&
            canGenerateAiInsight(widgetConfiguration?.type) &&
            hasWidgetData(widgetChartData),
          icon: 'Sparkles',
          clickHandler: () => handleClickAiInsight(),
        },
        {
          title: t('settings'),
          isSupported: true,
          isAvailableToUser: layoutProps?.canAdmin ?? false,
          icon: 'Settings',
          clickHandler: () => setSettingsOpened(!settingsOpened),
        },
        {
          title: t('export'),
          icon: 'Download',
          isSupported: true,
          isAvailableToUser: true,
          //availability depends on widget's type not permissions
          clickHandler: () => setConfigOverlayOpened(!configOverlayOpened),
        },
        {
          title: layoutWidget?.width === '100%' ? t('singleWidth') : t('doubleWidth'),
          isSupported: true,
          isAvailableToUser,
          icon: 'SquareSplitHorizontal',
          clickHandler: () => onEditDimension(false, true),
        },
        {
          title: layoutWidget?.height === '100%' ? t('singleHeight') : t('doubleHeight'),
          isSupported: true,
          isAvailableToUser,
          icon: 'SquareSplitVertical',
          clickHandler: () => onEditDimension(true, false),
        },
        {
          title: t('delete'),
          isSupported: true,
          isAvailableToUser,
          icon: 'Trash',
          clickHandler: () => setShowSaveModal(true),
        },
      ] satisfies MenuItem[],
    // eslint-disable-next-line react-hooks/exhaustive-deps -- aiEnabled, configOverlayOpened, handleClickAiInsight, handleClickRecordInsight, isConfigFetching, onEditDimension, widgetChartData, and widgetConfiguration props are intentionally excluded to prevent excessive re-renders
    [settingsOpened, layoutProps, layoutWidget, isAvailableToUser, exportWidgetHandler, isTable]
  );

  const exportSubMenuItems = [
    {
      title: 'CSV',
      clickHandler: () => exportWidgetHandler('CSV', true),
      isSupported: false,
      isAvailableToUser: true,
    },
    {
      title: 'Excel',
      clickHandler: () => exportWidgetHandler('EXCEL', true),
      isSupported: true,
      isAvailableToUser: isTable,
    },
    {
      title: 'PDF',
      clickHandler: () => {
        if (BACKGROUND_EXPORT_WIDGET_TYPES.includes(String(widgetConfiguration?.type))) {
          exportWidgetHandler('PDF', false);
        } else {
          setConfigOverlayOpened(!configOverlayOpened);
          setShowFooter(false);
          setTimeout(() => {
            captureScreenshot();
          }, 200);
        }
      },
      isSupported: true,
      isAvailableToUser: true,
    },
  ];

  const onDeleteHandler = () => {
    const widgetLayoutId = layoutWidget?.id;
    if (widgetLayoutId) {
      const widgetRef = document.getElementById(widgetLayoutId);
      deleteWidget(widgetLayoutId);
      if (widgetRef) {
        widgetRef.remove();
      }
    }
  };

  const onEditDimension = useCallback(
    (updateHeight: boolean, updateWidth: boolean) => {
      if (layoutProps && layoutWidget) {
        const { height, width } = layoutWidget;
        const updatedData = {
          ...layoutWidget,
          width: updateWidth ? (width === '100%' ? '50%' : '100%') : width,
          height: updateHeight ? (height === '100%' ? '50%' : '100%') : height,
        };
        setLayoutWidget(updatedData);
        updateWidgetLayout(updatedData);

        dispatch(
          setWidgetWidthHeight({
            id: updatedData.id,
            updateHeight,
            updateWidth,
          })
        );
      }
    },
    [layoutProps, layoutWidget, dispatch, updateWidgetLayout]
  );

  const onHideModal = () => {
    setShowSaveModal(false);
  };

  const handleClickAiInsight = async () => {
    dispatch(
      setWidgetAiInsightPanelOpened({
        show: true,
        title: widgetConfiguration?.title,
        isRecordInsight: false,
        value: undefined,
        widgetData: {
          widgetId: widgetId,
          body: getWidgetDataBody(widgetChartData),
        },
      })
    );
  };

  const handleClickRecordInsight = useCallback(
    async (widgetId?: string, widgetTitle?: string) => {
      if (!widgetId) {
        toast.error('Widget id is not specified');
        return;
      }
      if (creatingJobsWidgetIds.current.includes(widgetId)) {
        toast.error('Record insight job is already in progress for this widget');
        return;
      }
      creatingJobsWidgetIds.current.push(widgetId);
      const jobs = runningJobsRef.current;

      if (jobs.some((job) => job.widgetId === widgetId && job.status === JobStatus.InProgress)) {
        toast.error('Record insight job is already in progress for this widget');
        return;
      }
      const payload = { widgetId: widgetId };

      const jobStartedDate = new Date().toISOString();
      try {
        dispatch(submitJob({ name: widgetTitle, widgetId: widgetId, startedDate: jobStartedDate }));
        const id = (await startRecordsInsightsAnalysisJob(payload).unwrap()) ?? {};

        if (id) {
          dispatch(setJobStatus({ widgetId: widgetId, id: id, startedDate: jobStartedDate }));
        } else {
          toast.error('Unable to generate record insight', { id: toastId.current });
          dispatch(
            setJobStatus({
              widgetId: widgetId,
              status: JobStatus.Failed,
              completedDate: new Date().toISOString(),
              startedDate: jobStartedDate,
            })
          );
        }
      } catch (error) {
        const errorMessage = (error as Record<string, unknown> | null)?.data;

        if (typeof errorMessage === 'string') {
          toast.error(errorMessage, { id: toastId.current });
        } else {
          toast.error('Unable to generate record insight', { id: toastId.current });
        }
        dispatch(
          setJobStatus({
            widgetId: widgetId,
            status: JobStatus.Failed,
            completedDate: new Date().toISOString(),
            startedDate: jobStartedDate,
          })
        );
      } finally {
        creatingJobsWidgetIds.current = creatingJobsWidgetIds.current.filter((id) => id !== widgetId);
      }
    },
    [startRecordsInsightsAnalysisJob, runningJobsRef, dispatch]
  );

  // Keep the ref always up to date
  useEffect(() => {
    runningJobsRef.current = runningJobs;
  }, [runningJobs]);

  return (
    <>
      <div className="mt-auto flex flex-row justify-between">
        {layoutWidget?.widget?.widgetData?.customFilterField && showFooter ? (
          <OdinCustomFilters
            id={widgetId}
            customFiltersData={customFiltersData}
            setCustomFiltersData={setCustomFiltersData}
            onFilterStateChanged={refetch}
          />
        ) : null}
        <div className="ml-auto flex flex-row justify-end gap-2">
          {menuOptions
            .filter((option) => option.isAvailableToUser)
            .map((menuOption) => {
              return (
                <div
                  key={menuOption.title}
                  onClick={menuOption.clickHandler}
                  className="m-0.5 ml-auto flex-none"
                  ref={iconRef}
                  title={menuOption.title}
                >
                  <OdinIcon
                    icon={menuOption.icon ?? 'ClipboardList'}
                    size={OdinIconSize.Small}
                    className={showFooter ? odinIconStyle : `text-mono-1`}
                  />
                </div>
              );
            })}
        </div>
      </div>
      <OverlayPanel
        target={iconRef.current}
        visible={configOverlayOpened}
        hidden={() => setConfigOverlayOpened(false)}
        mountOnEnter={false}
        unmountOnExit={false}
        shouldScrollCloseOverlay
        shouldCheckZIndex
        shouldUseCreatePortal={false}
        transitionDuration={0}
        elementAlignment={DomElementAlignment.BottomLeft}
        targetPosition={DomTargetPosition.BottomLeft}
        className="OverlayPanel flex w-36 rounded-md bg-gray-4 shadow-md"
      >
        <SettingsMenu items={exportSubMenuItems} />
      </OverlayPanel>
      {widgetConfiguration && (
        <WidgetSettingsDialog
          aiEnabled={Boolean(aiEnabled)}
          widgetConfiguration={widgetConfiguration}
          visible={settingsOpened}
          onDialogHidden={() => setSettingsOpened(false)}
        />
      )}
      <ConfirmationWindow
        onSaveHandler={onDeleteHandler}
        showWindow={showSaveModal}
        onDiscardChanges={{
          onHideModal,
          onDiscard: onHideModal,
        }}
        title={isDeleting ? t('deleting') : t('deleteWidget')}
        text={t('confirmDelete')}
        discard={t('no')}
        save={t('yes')}
      />
    </>
  );
}
